/**
 * O valor no quadro do loop e o frescor do dado — funções puras (testadas em
 * `ar.test.ts`).
 *
 * Os três tipos de ponto têm relógios diferentes, e cada um casa com o quadro do
 * satélite do jeito que o dado dela permite — sempre com a mesma regra de
 * fundo: **nunca um valor do futuro com o horário do quadro, e sem valor
 * perto o bastante, "sem dado" (nunca zero)**.
 *
 * - **Sensores (grade de 5 min)**: a leitura mais recente ATÉ o quadro, de no
 *   máximo 30 min antes. A rede publica com atraso (medido: 20–25 min em
 *   23/09/2026), e sem essa folga o quadro mais novo do satélite ficaria
 *   sempre sem dado; mais que 30 min, a fumaça já andou. Sensor consultado de
 *   hora em hora (PurpleAir pela API, OpenAQ) traz a própria folga (`tolMs`).
 * - **Estações oficiais (horárias)**: a medição horária mais recente até o
 *   quadro, de no máximo 3 h antes. O MonitorAr fecha a hora com 1–2 h de
 *   atraso, e o índice do MP2,5 já é uma média móvel de 24 h: varia devagar.
 * - **Modelo CAMS (horário)**: a hora cheia que contém o quadro (12:40 → a
 *   das 12:00). Não a mais próxima: o espelho publica só até a hora corrente
 *   (sem previsão), e a "mais próxima" de 12:40 seria a das 13:00, que não
 *   existe — a grade sumia na segunda metade de toda hora.
 *
 * **No mapa vale a regra dos pluviômetros** (23/09/2026, a pedido do
 * usuário): o ponto mostra a leitura mais recente até o quadro enquanto ela
 * tiver até 24 h (`VALIDADE_MAPA_MS`) — sem buracos entre uma leitura e a
 * seguinte, que confundiam "rede atrasada" com "sensor parado". Só depois de
 * 24 h sem leitura o símbolo muda para "sem dado". As tolerâncias acima
 * continuam valendo para a ficha dizer quão velha é a leitura e para o gráfico.
 */
import { HOUR_MS, MINUTE_MS } from '../lib/time';

export const TOLERANCIA_SENSOR_MS = 30 * MINUTE_MS;
export const TOLERANCIA_ESTACAO_MS = 3 * HOUR_MS;
/** Até quanto tempo depois da leitura o ponto continua pintado no mapa. */
export const VALIDADE_MAPA_MS = 24 * HOUR_MS;

export interface Achado<T = number> {
  valor: T;
  /** Instante do valor (epoch ms). */
  t: number;
  /** Índice na série. */
  i: number;
}

/**
 * O valor mais recente da série regular até `instante`, de no máximo `tolMs`
 * antes dele. `null` sem instante, antes da série ou longe demais.
 */
export function ultimoAte<T>(
  serie: readonly (T | null)[] | undefined,
  t0: number,
  passoMs: number,
  instante: number | null,
  tolMs: number,
): Achado<T> | null {
  if (!serie || serie.length === 0 || instante === null || !Number.isFinite(instante) || !(passoMs > 0)) return null;
  // Uma folga de 1 ms absorve o ponto flutuante quando o quadro cai em cima do horário da grade.
  const k = Math.floor((instante - t0 + 1) / passoMs);
  if (k < 0) return null;
  for (let i = Math.min(k, serie.length - 1); i >= 0; i--) {
    const t = t0 + i * passoMs;
    if (instante - t > tolMs) return null;
    const v = serie[i];
    if (v !== null && v !== undefined && (typeof v !== 'number' || Number.isFinite(v))) return { valor: v, t, i };
  }
  return null;
}

/** O valor do modelo na hora cheia que contém o quadro, ou `null` fora da janela ou sem valor. */
export function horaDoModelo(
  serie: readonly (number | null)[] | undefined,
  h0: number,
  instante: number | null,
): Achado | null {
  const i = indiceQueContem(serie?.length ?? 0, h0, HOUR_MS, instante);
  if (i < 0) return null;
  const v = serie![i];
  return typeof v === 'number' && Number.isFinite(v) ? { valor: v, t: h0 + i * HOUR_MS, i } : null;
}

/** Índice da grade que CONTÉM o instante (para a marca no gráfico), ou -1. */
export function indiceQueContem(n: number, t0: number, passoMs: number, instante: number | null): number {
  if (n <= 0 || instante === null || !Number.isFinite(instante) || !(passoMs > 0)) return -1;
  const i = Math.floor((instante - t0 + 1) / passoMs);
  return i >= 0 && i < n ? i : -1;
}

/**
 * Série horária espalhada numa grade mais fina (a média do município sobre a
 * grade de 5 min do sensor, para o gráfico): cada fatia fina recebe o valor da
 * hora que a contém.
 */
export function espalharHoras(
  horaria: readonly (number | null)[],
  h0: number,
  t0: number,
  passoMs: number,
  n: number,
): (number | null)[] {
  const out: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const h = Math.floor((t0 + i * passoMs - h0) / HOUR_MS);
    const v = h >= 0 && h < horaria.length ? horaria[h] : null;
    out[i] = typeof v === 'number' && Number.isFinite(v) ? v : null;
  }
  return out;
}

/**
 * Para o gráfico de um sensor de consulta horária: cada leitura se estende
 * pelos horários seguintes até `tolMs` (ou até a próxima leitura) — sem isso,
 * uma leitura por hora numa grade de 5 min vira pontinhos invisíveis. Só
 * desenho: a média de 24 h e o mapa usam a série crua.
 */
export function estenderLeituras(serie: readonly (number | null)[], passoMs: number, tolMs: number): (number | null)[] {
  const k = Math.max(0, Math.floor(tolMs / passoMs));
  const out = [...serie];
  let ultimo: number | null = null;
  let desde = 0;
  for (let i = 0; i < serie.length; i++) {
    const v = serie[i];
    if (typeof v === 'number' && Number.isFinite(v)) {
      ultimo = v;
      desde = 0;
    } else if (ultimo !== null && ++desde <= k) out[i] = ultimo;
  }
  return out;
}

/** Os valores das últimas 24 h até o índice `i` (inclusive), numa grade de `passoMs`. */
export function janela24h<T>(serie: readonly (T | null)[], i: number, passoMs: number): (T | null)[] {
  const n = Math.round((24 * HOUR_MS) / passoMs);
  if (i < 0) return [];
  const inicio = i - n + 1;
  const out: (T | null)[] = [];
  for (let k = inicio; k <= i; k++) out.push(k >= 0 && k < serie.length ? (serie[k] ?? null) : null);
  return out;
}

// ---------------------------------------------------------------------------
// Frescor do espelho

/** O espelho roda de 5 em 5 min. Três rodadas perdidas: avisa. */
export const ATRASO_AVISO_MIN = 20;
/** Seis horas: o que está no mapa já não é o ar de agora. */
export const ATRASO_GRAVE_MIN = 6 * 60;

function quanto(min: number): string {
  return min >= 120 ? `${Math.floor(min / 60)} h` : `${Math.round(min)} min`;
}

/** Aviso sobre o arquivo publicado, ou `null` quando está em dia. */
export function avisoDeFrescor(gerado: number | null, agora: number = Date.now()): string | null {
  if (gerado === null || !Number.isFinite(gerado)) return null;
  const min = Math.max(0, (agora - gerado) / MINUTE_MS);
  if (min >= ATRASO_GRAVE_MIN) return `Qualidade do ar parada há ${quanto(min)}. O que está no mapa não é o ar de agora.`;
  if (min >= ATRASO_AVISO_MIN) return `Qualidade do ar sem atualizar há ${quanto(min)} — o normal é a cada 5 min.`;
  return null;
}

/** Leitura mais velha que a janela (48 h): o sensor ou a estação está parado. */
export function parado(ultima: number | null, agora: number = Date.now()): boolean {
  return ultima === null || !Number.isFinite(ultima) || agora - ultima > 48 * HOUR_MS;
}
